import React, { useEffect, useRef } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import { createBlogExtensions } from './blogExtensions'
import styles from './BlogEditor.module.scss'

const emptyDoc = () => ({ type: 'doc', content: [{ type: 'paragraph' }] })

function BlogTextBlock({ block, onChange, onFocusEditor }) {
  const onChangeRef = useRef(onChange)
  const onFocusEditorRef = useRef(onFocusEditor)
  const blockRef = useRef(block)

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  useEffect(() => {
    onFocusEditorRef.current = onFocusEditor
  }, [onFocusEditor])

  useEffect(() => {
    blockRef.current = block
  }, [block])

  const editor = useEditor({
    extensions: createBlogExtensions({
      placeholder:
        block.type === 'heading'
          ? 'Заголовок…'
          : block.type === 'blockquote'
            ? 'Цитата…'
            : block.type === 'bulletList'
              ? 'Пункт списку…'
              : block.type === 'orderedList'
                ? 'Пункт списку…'
                : 'Текст…',
    }),
    content: block.content || emptyDoc(),
    editorProps: {
      attributes: {
        class: styles.prose,
      },
      handleDOMEvents: {
        dragstart: (_view, event) => {
          event.preventDefault()
          return true
        },
        dragenter: (_view, event) => {
          event.preventDefault()
          if (event.dataTransfer) event.dataTransfer.dropEffect = 'none'
          return true
        },
        dragover: (_view, event) => {
          event.preventDefault()
          if (event.dataTransfer) event.dataTransfer.dropEffect = 'none'
          return true
        },
        drop: (_view, event) => {
          event.preventDefault()
          return true
        },
      },
    },
    onUpdate: ({ editor: current }) => {
      // Dedicated quote block must stay a blockquote if TipTap tries to unwrap it.
      if (blockRef.current.type === 'blockquote' && !current.isActive('blockquote')) {
        current.commands.setBlockquote()
      }
      onChangeRef.current({
        ...blockRef.current,
        content: current.getJSON(),
      })
    },
    onFocus: ({ editor: current }) => {
      onFocusEditorRef.current?.(current)
    },
    onSelectionUpdate: ({ editor: current }) => {
      onFocusEditorRef.current?.(current)
    },
  })

  // Re-hydrate only when switching to another block id.
  // setContent(..., false) does not emit update — do not skip the next real edit.
  useEffect(() => {
    if (!editor) return
    const next = block.content || emptyDoc()
    editor.commands.setContent(next, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, editor])

  // Keep TipTap heading level in sync with chrome control.
  useEffect(() => {
    if (!editor || block.type !== 'heading') return
    const level = block.level || 2
    if (editor.isActive('heading', { level })) return
    editor.chain().setHeading({ level }).run()
  }, [block.level, block.type, editor])

  useEffect(() => {
    return () => {
      if (editor) editor.destroy()
    }
  }, [editor])

  return (
    <div
      className={styles.textBlock}
      onFocus={() => {
        if (editor) onFocusEditorRef.current?.(editor)
      }}
    >
      <EditorContent editor={editor} />
    </div>
  )
}

export default BlogTextBlock
